import { browser } from '$app/environment';
import type { Language } from '$lib/stores/i18n';
import { derived, writable } from 'svelte/store';

const STORAGE_KEY = '3dai_generation_notifications_v1';
const MAX_ITEMS = 50;

export type GenerationNotificationItem = {
	id: string;
	promptText: string;
	taskType: string;
	createdAt: number;
	read: boolean;
};

function isValidItem(x: unknown): x is GenerationNotificationItem {
	if (!x || typeof x !== 'object') return false;
	const o = x as Record<string, unknown>;
	return (
		typeof o.id === 'string' &&
		typeof o.promptText === 'string' &&
		typeof o.taskType === 'string' &&
		typeof o.createdAt === 'number' &&
		typeof o.read === 'boolean'
	);
}

function loadFromStorage(): GenerationNotificationItem[] {
	if (!browser) return [];
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return [];
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(isValidItem);
	} catch {
		return [];
	}
}

function saveToStorage(items: GenerationNotificationItem[]) {
	if (!browser) return;
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
	} catch {
		/* private mode / quota */
	}
}

const initial: GenerationNotificationItem[] = browser ? loadFromStorage() : [];

export const generationNotifications = writable<GenerationNotificationItem[]>(initial);

if (browser) {
	generationNotifications.subscribe((v) => saveToStorage(v));
}

export const unreadGenerationNotificationCount = derived(generationNotifications, ($n) =>
	$n.filter((x) => !x.read).length
);

export function truncatePrompt(text: string, maxLen = 80): string {
	const t = (text || '').trim();
	if (t.length <= maxLen) return t;
	return t.slice(0, maxLen - 1) + '…';
}

export function notifyGenerationComplete(payload: {
	taskId: string;
	promptText?: string;
	taskType?: string;
}): void {
	const id = payload.taskId;
	if (!id) return;

	generationNotifications.update((list) => {
		const idx = list.findIndex((x) => x.id === id);
		const next: GenerationNotificationItem = {
			id,
			promptText: payload.promptText || '',
			taskType: payload.taskType || 'text-to-3d',
			createdAt: Date.now(),
			read: false
		};

		if (idx !== -1) {
			const copy = [...list];
			const prev = copy[idx];
			copy[idx] = {
				...next,
				read: prev.read
			};
			return copy;
		}
		return [next, ...list].slice(0, MAX_ITEMS);
	});
}

export function markGenerationNotificationRead(id: string): void {
	generationNotifications.update((list) =>
		list.map((x) => (x.id === id ? { ...x, read: true } : x))
	);
}

export function markAllGenerationNotificationsRead(): void {
	generationNotifications.update((list) => list.map((x) => ({ ...x, read: true })));
}

export function formatRelativeTime(
	createdAt: number,
	lang: Language,
	t: (key: string, params?: Record<string, string | number>) => string
): string {
	const now = Date.now();
	const sec = Math.floor((now - createdAt) / 1000);
	if (sec < 60) return t('notifications.timeAgoJustNow');
	const min = Math.floor(sec / 60);
	if (min < 60) return t('notifications.timeAgoMinutes', { count: min });
	const hours = Math.floor(min / 60);
	if (hours < 24) return t('notifications.timeAgoHours', { count: hours });
	const days = Math.floor(hours / 24);
	return t('notifications.timeAgoDays', { count: days });
}
